import type { Entry } from '@iculogbook/shared';

import { buildDocument, pdfFilename } from './document.ts';
import type { PdfOptions } from './document.ts';

/**
 * Turning the document definition into a file.
 *
 * pdfmake and its fonts are loaded on demand rather than bundled into the app
 * shell: they are the largest dependency in the project and most sessions never
 * generate a PDF. A trainee logging an arterial line at 3am should not have
 * waited for a typesetting engine to download.
 */

type FontFamily = { normal: string; bold: string; italics: string; bolditalics: string };

type PdfMake = {
  createPdf: (definition: Record<string, unknown>) => {
    download: (filename: string) => void;
    getBlob: (callback: (blob: Blob) => void) => void;
  };
  /** The supported way to register fonts in pdfmake 0.3. */
  addVirtualFileSystem?: (vfs: unknown) => void;
  vfs?: unknown;
  fonts?: Record<string, FontFamily>;
};

/*
 * The serif, for titles, section heads and the italic provenance line.
 *
 * Served from `client/public/fonts` and fetched at generation time rather than
 * base64'd into the bundle, so the binaries stay binaries in the repository
 * instead of becoming a quarter-megabyte of generated source. They are precached
 * by the service worker — `ttf` is in `injectManifest.globPatterns` for exactly
 * this reason — so a PDF still generates with no network, which PDF_SPEC.md
 * requires.
 */
const SERIF_FILES = {
  'Newsreader-Regular.ttf': '/fonts/Newsreader-Regular.ttf',
  'Newsreader-SemiBold.ttf': '/fonts/Newsreader-SemiBold.ttf',
  'Newsreader-Italic.ttf': '/fonts/Newsreader-Italic.ttf',
} as const;

/** A font file as the base64 string pdfmake's virtual filesystem expects. */
async function base64(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Font ${url} responded ${String(response.status)}`);

  const bytes = new Uint8Array(await response.arrayBuffer());

  /*
   * Chunked rather than one spread call: a 50KB font is 50,000 arguments to
   * `String.fromCharCode`, which overflows the call stack in Safari.
   */
  let binary = '';
  for (let index = 0; index < bytes.length; index += 8192) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
  }

  return btoa(binary);
}

let loaded: Promise<PdfMake> | null = null;

async function pdfMake(): Promise<PdfMake> {
  loaded ??= (async () => {
    const [{ default: make }, { default: fonts }, ...serif] = await Promise.all([
      import('pdfmake/build/pdfmake'),
      import('pdfmake/build/vfs_fonts'),
      ...Object.values(SERIF_FILES).map(base64),
    ]);

    const instance = make as unknown as PdfMake;

    /*
     * Roboto ships with pdfmake and is embedded in the document. Self-hosted by
     * definition — nothing here fetches a font from a CDN, ever.
     *
     * `addVirtualFileSystem` is the supported registration in pdfmake 0.3.
     * Assigning `.vfs` looks equivalent and is not: it left the bold face
     * unregistered, and generation failed with "Roboto-Medium.ttf not found in
     * virtual file system" only once a document used bold — which every one of
     * ours does.
     */
    const base = (fonts as unknown as { vfs?: Record<string, string> }).vfs ?? fonts;

    // Roboto's files plus the three Newsreader instances, in one filesystem.
    const names = Object.keys(SERIF_FILES);
    const vfs: Record<string, string> = { ...(base as Record<string, string>) };
    names.forEach((name, index) => {
      const encoded = serif[index];
      if (encoded !== undefined) vfs[name] = encoded;
    });

    if (typeof instance.addVirtualFileSystem === 'function') instance.addVirtualFileSystem(vfs);
    else instance.vfs = vfs;

    /*
     * Declaring `fonts` is required once a document names a family other than
     * the default. Roboto has to be redeclared here too: assigning the map
     * replaces pdfmake's built-in one rather than extending it, so omitting it
     * would break every body paragraph in the document.
     *
     * Newsreader has no bold italic instance and the document never asks for
     * one; `bolditalics` points at the italic so a stray combination degrades
     * to an upright-weight italic instead of throwing mid-generation.
     */
    instance.fonts = {
      Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Medium.ttf',
        italics: 'Roboto-Italic.ttf',
        bolditalics: 'Roboto-MediumItalic.ttf',
      },
      Newsreader: {
        normal: 'Newsreader-Regular.ttf',
        bold: 'Newsreader-SemiBold.ttf',
        italics: 'Newsreader-Italic.ttf',
        bolditalics: 'Newsreader-Italic.ttf',
      },
    };

    return instance;
  })();

  return loaded;
}

export async function generatePdf(
  entries: readonly Entry[],
  options: PdfOptions,
): Promise<{ filename: string }> {
  const make = await pdfMake();
  const filename = pdfFilename(options.displayName, options.range);

  make.createPdf(buildDocument(entries, options)).download(filename);

  return { filename };
}

/** The same document as a blob, for tests and for the Playwright download check. */
export async function generatePdfBlob(
  entries: readonly Entry[],
  options: PdfOptions,
): Promise<Blob> {
  const make = await pdfMake();

  return new Promise((resolve) => {
    make.createPdf(buildDocument(entries, options)).getBlob(resolve);
  });
}
